import React, { useEffect, useState } from "react";
import {
  MenuItem,
  Paper,
  ListItemText,
  ListItemIcon,
  Divider,
  Modal,
  Box,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight"; // Flecha para indicar submenús
import { useTranslation } from "react-i18next";

const normalizeShortcut = (s) => {
  if (!s) return null;
  if (typeof s === "string") return { key: s.toLowerCase() };
  return { ...s, key: (s.key || "").toLowerCase() };
};

// Format a human-readable shortcut string, e.g., "Ctrl+Shift+N"
const formatShortcut = (s) => {
  const sc = normalizeShortcut(s);
  if (!sc) return "";
  const parts = [];
  if (sc.ctrl) parts.push("Ctrl");
  if (sc.alt) parts.push("Alt");
  if (sc.shift) parts.push("Shift");
  if (sc.meta) parts.push("Meta"); // or "⌘" if you detect macOS
  if (sc.key) parts.push(sc.key.toUpperCase());
  return parts.join("+");
};

const NestedMenu = ({ actions, onClose, isLeftSide, isTopSide = true }) => {
  const { t } = useTranslation("main");
  const [submenuActions, setSubmenuActions] = useState(null);
  const [submenuAnchor, setSubmenuAnchor] = useState(null);
  const isMobile = window.innerWidth < 960;

  const [interactionLock, setInteractionLock] = useState(true);

  useEffect(() => {
    if (isMobile) {
      // ⚠️ Evita interacciones durante los primeros 400ms tras abrir
      const timeout = setTimeout(() => setInteractionLock(false), 400);
      return () => clearTimeout(timeout);
    }
  }, []);

  if (isMobile)
    return (
      <>
        <Modal
          open={!submenuActions}
          onClose={() => {
            console.log("ON CLOSE");
            onClose();
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <Paper
              elevation={3}
              style={{
                padding: "5px",
                maxWidth: "90vw",
                maxHeight: "90vh",
                maxHeight: "90dvh",
                width: "max-content",
              }}
            >
              {actions.map((act, index) => {
                const { action, items, icon, label, style, divider } = act;
                return (
                  <React.Fragment key={index}>
                    {(action || items || icon || label) && (
                      <MenuItem
                        onClick={(event) => {
                          if (interactionLock) return;

                          if (action) {
                            action();
                            onClose(); // Cierra el menú al ejecutar la acción
                          }
                        }}
                        onMouseEnter={(event) => {
                          if (interactionLock) return;
                          if (items) {
                            setSubmenuActions(items);
                          } else {
                            setSubmenuActions(null); // Si el item no tiene submenús, cierra el submenú actual
                          }
                        }}
                        sx={{
                          position: "relative",
                          display: "flex",
                          gap: "1rem",
                          alignItems: "center",
                          justifyContent: "space-between",
                          ...style,
                          ...(items && { paddingRight: "0" }),
                          ...(!action &&
                            !items && {
                              cursor: "auto",
                              pointerEvents: "none",
                            }),
                        }}
                      >
                        {icon && (
                          <ListItemIcon
                            style={{
                              minWidth: "0px",
                              borderRadius: "4px",
                              overflow: "hidden",
                            }}
                          >
                            {icon}
                          </ListItemIcon>
                        )}
                        {label && (
                          <ListItemText style={{ whiteSpace: "break-spaces" }}>
                            {t(label)}
                          </ListItemText>
                        )}

                        {items && (
                          <ListItemIcon style={{ minWidth: "0px" }}>
                            <ChevronRightIcon fontSize="small" />
                          </ListItemIcon>
                        )}
                      </MenuItem>
                    )}
                    {divider && <Divider style={{ width: "100%" }} />}
                  </React.Fragment>
                );
              })}
            </Paper>
          </Box>
        </Modal>
        {submenuActions && (
          <NestedMenu actions={submenuActions} onClose={onClose} />
        )}
      </>
    );
  else
    return (
      <Paper
        elevation={3}
        style={{
          padding: "5px",
          position: "relative",
          borderRadius: `${isLeftSide && isTopSide ? "0" : "8px"} ${!isLeftSide && isTopSide ? "0" : "8px"} ${!isLeftSide && !isTopSide ? "0" : "8px"} ${isLeftSide && !isTopSide ? "0" : "8px"}`,
        }}
      >
        {actions.map((act, index) => {
          const { action, items, icon, label, style, divider } = act;

          const shortcutText = formatShortcut(act.shortcut);
          return (
            <React.Fragment key={index}>
              {(action || items || icon || label) && (
                <MenuItem
                  onClick={(event) => {
                    if (action) {
                      action();
                      onClose(); // Cierra el menú al ejecutar la acción
                    }
                  }}
                  onMouseEnter={(event) => {
                    const processedActions = isTopSide
                      ? actions
                      : actions.slice().reverse();

                    const dividersBefore = processedActions
                      .slice(0, isTopSide ? index : actions.length - index) // Tomamos solo los elementos antes del actual
                      .filter((item) => item.divider).length; // Filtramos los que tienen divider: true // Contamos cuántos hay

                    const pixelsPerAction = 36;
                    const pixelsPerDivider = 16;
                    if (items) {
                      setSubmenuActions(items);
                      setSubmenuAnchor({
                        ...(isTopSide
                          ? {
                              top:
                                pixelsPerAction * index -
                                pixelsPerDivider * dividersBefore,
                            }
                          : {
                              bottom:
                                pixelsPerAction * (actions.length - index - 1) -
                                pixelsPerDivider * dividersBefore,
                            }), // Mueve el submenú a la derecha

                        ...(isLeftSide ? { left: "100%" } : { right: "100%" }), // Mueve el submenú a la derecha
                      });
                    } else {
                      setSubmenuActions(null); // Si el item no tiene submenús, cierra el submenú actual
                    }
                  }}
                  sx={{
                    position: "relative",
                    display: "flex",
                    gap: "1rem",
                    alignItems: "center",
                    justifyContent: "space-between",
                    ...style,
                    ...(items && { paddingRight: "0" }),
                    ...(!action &&
                      !items && { cursor: "auto", pointerEvents: "none" }),
                  }}
                >
                  {icon && ( // Si tiene submenús, muestra la flecha
                    <ListItemIcon
                      style={{
                        minWidth: "0px",
                        borderRadius: "4px",
                        overflow: "hidden",
                      }}
                    >
                      {icon}
                    </ListItemIcon>
                  )}
                  {label && <ListItemText>{t(label)}</ListItemText>}

                  {shortcutText ? (
                    <span
                      style={{ marginLeft: "auto", opacity: 0.7, fontSize: 12 }}
                    >
                      {shortcutText}
                    </span>
                  ) : null}

                  {items && (
                    <ListItemIcon style={{ minWidth: "0px" }}>
                      <ChevronRightIcon fontSize="small" />
                    </ListItemIcon>
                  )}
                </MenuItem>
              )}
              {divider && <Divider style={{ width: "100%" }} />}
            </React.Fragment>
          );
        })}

        {/* Renderiza el submenú dentro del mismo `Paper`, sin usar `Menu` de Material-UI */}
        {submenuActions && (
          <div
            style={{
              position: "absolute",
              ...(isTopSide
                ? { top: submenuAnchor.top }
                : { bottom: submenuAnchor.bottom }), // Mueve el submenú a la derecha
              ...(isLeftSide
                ? { left: submenuAnchor.left }
                : { right: submenuAnchor.right }), // Mueve el submenú a la derecha

              zIndex: 10,
            }}
          >
            <NestedMenu
              actions={submenuActions}
              onClose={onClose}
              isLeftSide={isLeftSide}
              isTopSide={isTopSide}
            />
          </div>
        )}
      </Paper>
    );
};

export default NestedMenu;
